import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { LoadingState } from "@/components/common/EmptyState";
import DashboardLayout from "@/components/layout/DashboardLayout";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Cases from "@/pages/Cases";
import CaseDetail from "@/pages/CaseDetail";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Hearings from "@/pages/Hearings";
import MyDiary from "@/pages/MyDiary";
import Tasks from "@/pages/Tasks";
import Documents from "@/pages/Documents";
import Reports from "@/pages/Reports";
import Team from "@/pages/Team";
import Notifications from "@/pages/Notifications";
import Subscription from "@/pages/Subscription";
import ClientPortal from "@/pages/ClientPortal";
import Landing from "@/pages/Landing";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import RefundPolicy from "@/pages/RefundPolicy";
import Contact from "@/pages/Contact";
import PricingPage from "@/pages/PricingPage";
import SecurityPage from "@/pages/SecurityPage";
import PaymentPage from "@/pages/PaymentPage";
import AcceptInvite from "@/pages/AcceptInvite";
import OwnerLayout from "@/components/platform/OwnerLayout";
import PlatformDashboard from "@/pages/platform/PlatformDashboard";
import PlatformOrganizations from "@/pages/platform/PlatformOrganizations";
import PlatformOrgDetail from "@/pages/platform/PlatformOrgDetail";
import PlatformUsers from "@/pages/platform/PlatformUsers";
import Settings from "@/pages/Settings";

const homeFor = (role) =>
  role === "platform_owner" ? "/platform" : role === "client" ? "/portal" : "/dashboard";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center"><LoadingState /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={homeFor(user.role)} replace />;
  }
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center"><LoadingState /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user.role)} replace />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="/portal" element={<ProtectedRoute roles={["client"]}><ClientPortal /></ProtectedRoute>} />

      <Route path="/platform" element={
        <ProtectedRoute roles={["platform_owner"]}><OwnerLayout /></ProtectedRoute>
      }>
        <Route index element={<PlatformDashboard />} />
        <Route path="organizations" element={<PlatformOrganizations />} />
        <Route path="organizations/:id" element={<PlatformOrgDetail />} />
        <Route path="users" element={<PlatformUsers />} />
      </Route>

      <Route element={<ProtectedRoute roles={["firm_admin", "advocate", "staff"]}><DashboardLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/diary" element={<MyDiary />} />
        <Route path="/cases" element={<Cases />} />
        <Route path="/cases/:id" element={<CaseDetail />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/clients/:id" element={<ClientDetail />} />
        <Route path="/hearings" element={<Hearings />} />
        <Route path="/calendar" element={<Hearings />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/team" element={<ProtectedRoute roles={["firm_admin"]}><Team /></ProtectedRoute>} />
        <Route path="/subscription" element={<Subscription />} />
        <Route path="/settings" element={<Settings />} />
      </Route>

      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/" element={<Landing />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/payment" element={<PaymentPage />} />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
